import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Youtube, Eye, ThumbsUp, MessageCircle, Clock, Sparkles, Quote, Lightbulb,
  Loader2, ArrowRight, Wand2,
} from "lucide-react";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatNumber = (num) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return String(num ?? 0);
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
}

export function SharedHookPage({ slug }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/shared/hooks/${slug}`, { withCredentials: false });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "error");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (!data && !error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="shared-loading">
        <div className="text-center">
          <Loader2 className="w-7 h-7 animate-spin text-red-500 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Loading teardown…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-100 flex items-center justify-center px-6" data-testid="shared-error">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">404</div>
          <h1 className="text-2xl font-semibold text-white mb-2">Teardown not found</h1>
          <p className="text-zinc-400 mb-6">This share link may have expired or been removed.</p>
          <Button onClick={() => (window.location.href = "/")} className="bg-red-600 hover:bg-red-500 text-white">
            Take me to VidROI
          </Button>
        </div>
      </div>
    );
  }

  const a = data.analysis;
  const transcript = data.transcript_full_300_words || data.transcript_first_150_words;

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 noise relative overflow-hidden" data-testid="shared-hook-page">
      {/* Ambient glow */}
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-red-600/20 blur-[140px] pointer-events-none" />

      {/* Top bar */}
      <header className="relative border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center shadow-[0_0_18px_rgba(255,0,0,0.25)]">
              <Youtube className="w-4.5 h-4.5 text-red-500" />
            </div>
            <div className="leading-tight">
              <div className="text-white font-semibold tracking-tight text-sm">
                Vid<span className="text-red-500 drop-shadow-[0_0_10px_rgba(255,0,0,0.5)]">ROI</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-medium">Hook Teardown</div>
            </div>
          </div>
          <Button
            onClick={startLogin}
            className="bg-white text-zinc-900 hover:bg-zinc-100 font-medium border border-white/10"
            data-testid="shared-cta-top"
          >
            Sign in <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </header>

      <main className="relative max-w-4xl mx-auto px-6 py-10 sm:py-14 animate-fade-up">
        {/* Eyebrow */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[11px] uppercase tracking-[0.2em] font-semibold mb-5">
          <Sparkles className="w-3.5 h-3.5" />
          Hook Teardown · {data.channel_name || "YouTube"}
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1] mb-6" data-testid="shared-title">
          {data.title}
        </h1>

        {/* Hero — thumbnail + video link + metrics */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-5 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-5">
            <a
              href={data.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="relative flex-shrink-0 group"
              data-testid="shared-watch-link"
            >
              <img
                src={data.thumbnail_url}
                alt={data.title}
                className="rounded-xl object-cover border border-white/10 w-full sm:w-72 aspect-video"
              />
              <div className="absolute inset-0 rounded-xl bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                <div className="bg-red-600 rounded-full p-3 shadow-[0_0_24px_rgba(255,0,0,0.5)]">
                  <Youtube className="w-6 h-6 text-white" />
                </div>
              </div>
            </a>

            <div className="flex-1 min-w-0">
              <div className="text-sm text-zinc-400 mb-3">{data.channel_name}</div>
              <div className="flex items-center flex-wrap gap-x-5 gap-y-1.5 text-sm text-zinc-400">
                <Metric icon={Eye} value={formatNumber(data.view_count)} label="views" />
                <Metric icon={ThumbsUp} value={formatNumber(data.like_count)} label="likes" />
                <Metric icon={MessageCircle} value={formatNumber(data.comment_count)} label="comments" />
                {data.duration && <Metric icon={Clock} value={data.duration} />}
              </div>
              <a
                href={data.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 font-medium text-sm transition-colors mt-4"
              >
                <Youtube className="w-4 h-4" />
                Watch on YouTube
              </a>
            </div>
          </div>
        </div>

        {/* AI Hook Analysis */}
        {a && (
          <section className="rounded-2xl border border-red-500/15 bg-red-500/[0.05] p-5 sm:p-6 mb-6" data-testid="shared-analysis">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-red-400" />
              <span className="font-semibold text-white">AI Hook Analysis</span>
              {a.hook_type && (
                <Badge className="bg-red-500/15 text-red-300 border border-red-500/25 hover:bg-red-500/15 ml-1" data-testid="shared-hook-type">
                  {a.hook_type}
                </Badge>
              )}
            </div>

            {a.one_sentence_summary && (
              <p className="text-[15px] text-zinc-200 mb-4 leading-relaxed">{a.one_sentence_summary}</p>
            )}

            {a.copywriting_frameworks?.length > 0 && (
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-semibold">Frameworks</div>
                <div className="flex flex-wrap gap-1.5">
                  {a.copywriting_frameworks.map((f, i) => (
                    <Badge key={`${f}-${i}`} className="bg-white/5 text-zinc-200 border border-white/10 hover:bg-white/5 font-normal">{f}</Badge>
                  ))}
                </div>
              </div>
            )}

            {a.patterns?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2 font-semibold flex items-center gap-1">
                  <Lightbulb className="w-3 h-3" /> Tactics spotted
                </div>
                <ul className="space-y-1.5">
                  {a.patterns.map((p, i) => (
                    <li key={`${p}-${i}`} className="text-sm text-zinc-200 flex items-start gap-2">
                      <span className="text-red-400 mt-0.5">•</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Transcript */}
        {transcript && (
          <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6 mb-10" data-testid="shared-transcript-section">
            <div className="flex items-center gap-2 mb-3">
              <Quote className="w-4 h-4 text-zinc-500" />
              <span className="font-medium text-zinc-300 text-sm">Opening Hook · {transcript.split(/\s+/).length} words</span>
            </div>
            <p className="text-zinc-200 leading-relaxed italic">"{transcript}"</p>
          </section>
        )}

        {/* CTA */}
        <section className="relative overflow-hidden rounded-2xl border border-white/10 p-6 sm:p-10 text-center bg-gradient-to-br from-red-500/10 to-transparent" data-testid="shared-cta-bottom">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-red-500/15 border border-red-500/30 text-red-400 mb-4">
            <Wand2 className="w-5 h-5" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight mb-2">
            Want to <span className="text-red-500">steal this hook</span> for your own video?
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-6 text-[15px] leading-relaxed">
            VidROI generates 3 hook variations adapted to your topic — using the same framework
            this creator used. Free to try. No credit card.
          </p>
          <Button
            onClick={startLogin}
            className="h-12 px-7 bg-red-600 hover:bg-red-500 text-white font-medium text-[15px] shadow-[0_0_24px_rgba(255,0,0,0.45)] border border-red-400/30"
            data-testid="shared-cta-signin-btn"
          >
            Continue with Google <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <div className="mt-4 text-[11px] uppercase tracking-[0.18em] text-zinc-600">
            Built with VidROI · YouTube revenue attribution
          </div>
        </section>
      </main>
    </div>
  );
}

function Metric({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-4 h-4 text-zinc-500" />
      <span>
        <span className="text-zinc-200">{value}</span>
        {label && <span className="ml-1">{label}</span>}
      </span>
    </div>
  );
}
